import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown, { type Components } from 'react-markdown';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContentWrapper } from '../../components/ContentWrapper/ContentWrapper';
import { Loader } from '../../components/Loader/Loader';
import { getPlatform, Platform } from '../../platform/platform';
import { getFaqJsUrl } from '../../utils/links';
import { loadFaqFromSite } from '../../utils/loadFaq';
import { openPlatformLink } from '../../utils/platform-links';
import styles from './FaqPage.module.scss';

type FaqPlatform = 'telegram' | 'max';

interface FaqJsonItem {
    q: string;
    a: string;
    'a-telegram'?: string;
    'a-max'?: string;
}

interface FaqItemView {
    id: string;
    question: string;
    answer: string;
}

const getFaqPlatform = (): FaqPlatform => (
    getPlatform() === Platform.Max ? 'max' : 'telegram'
);

const resolveHref = (href: string) => {
    if (!href || /^https?:\/\//i.test(href)) return href;
    if (href.startsWith('/')) {
        const base = process.env.REACT_APP_SITE_URL!;
        return `${base.replace(/\/$/, '')}${href}`;
    }
    return href;
};

const buildAnswer = (item: FaqJsonItem, platform: FaqPlatform) => {
    const extra = platform === 'max' ? (item['a-max'] || '') : (item['a-telegram'] || '');
    let text = item.a || '';
    if (text.includes('{{platform}}')) return text.replace('{{platform}}', extra);
    if (!extra) return text;
    return `${text}${text && !/\s$/.test(text) ? ' ' : ''}${extra}`;
};

const toMarkdown = (text: string) => text
    .split('\n')
    .map((line) => (line.trimStart().startsWith('•') ? `- ${line.trimStart().replace(/^•\s?/, '')}` : line))
    .join('\n');

const loadFaq = (fetchUrl: string) => loadFaqFromSite(fetchUrl);

const markdownComponents: Components = {
    a: ({ href, children, ...props }) => {
        const linkUrl = resolveHref(href || '');
        if (!linkUrl) {
            return <span>{children}</span>;
        }

        const isExternal = /^https?:\/\//i.test(linkUrl);

        if (!isExternal) {
            return (
                <a {...props} href={linkUrl} className={styles.link}>
                    {children}
                </a>
            );
        }

        return (
            <a
                {...props}
                href={linkUrl}
                className={styles.link}
                onClick={(e) => {
                    e.preventDefault();
                    openPlatformLink(linkUrl);
                }}
            >
                {children}
            </a>
        );
    },
};

export const FaqPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState<FaqItemView[]>([]);
    const [updatedAt, setUpdatedAt] = useState('');
    const [openId, setOpenId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const qParam = searchParams.get('q');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            const fetchUrl = getFaqJsUrl();
            const platform = getFaqPlatform();

            try {
                const data = await loadFaq(fetchUrl);
                if (cancelled) return;
                const mapped = (data.items || []).map((item: FaqJsonItem, i: number) => ({
                    id: `q${i + 1}`,
                    question: item.q || '',
                    answer: toMarkdown(buildAnswer(item, platform)),
                }));
                setItems(mapped);
                setUpdatedAt(data.updatedAt || '');
                setOpenId(qParam || mapped[0]?.id || null);
            } catch (e) {
                if (!cancelled) {
                    const msg = e instanceof Error ? e.message : 'ошибка загрузки';
                    setError(`Не удалось загрузить FAQ (${msg}).`);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [qParam]);

    useEffect(() => {
        if (!qParam || !items.length) return;
        setOpenId(qParam);
        document.getElementById(qParam)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [qParam, items]);

    return (
        <ContentWrapper full noTopBorder context="faq">
            <div className={styles.page}>
                <div className={styles.topBar}>
                    <button type="button" className={styles.back} onClick={() => navigate(-1)}>
                        <ArrowBackIcon className={styles.backIcon} />
                        Назад
                    </button>
                </div>

                <div className={styles.scrollContent}>
                <h2 className={styles.title}>Часто задаваемые вопросы</h2>
                {updatedAt && <div className={styles.updated}>(Обновлено {updatedAt})</div>}

                {loading && <Loader noBackground />}
                {error && <p className={styles.error}>{error}</p>}

                {!loading && !error && items.map((item) => (
                    <div key={item.id} id={item.id} className={styles.item}>
                        <button
                            type="button"
                            className={styles.question}
                            onClick={() => setOpenId(openId === item.id ? null : item.id)}
                        >
                            <span>{item.question}</span>
                            <span className={openId === item.id ? styles.arrowOpen : styles.arrow} />
                        </button>
                        {openId === item.id && (
                            <div className={styles.answer}>
                                <ReactMarkdown components={markdownComponents}>
                                    {item.answer}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>
                ))}
                </div>
            </div>
        </ContentWrapper>
    );
};
